// components/navbar/Navbar.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { RxCross1 } from 'react-icons/rx';
import { IoIosArrowDropdown, IoIosArrowDropup } from 'react-icons/io';
import Hamburger from '../../assets/Hamburger.svg';
import motorCraft_logo from '../../assets/motocraft_logo.svg';
import { useFsrAdminAccess } from '../../context/RoleContext';
import { useCdsid } from "../../context/AuthContext";
import { useMsal } from "@azure/msal-react";

// Helper function to clear a specific cookie (keep this here or move to a utility file)
function clearSpecificCookie(name: string, domain: string, path = '/') {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain};`;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
}

const clearAllAuthCookies = () => {
  const authCookies = [
    'FedAuth', 'FedAuth1', 'MSISAuth', 'MSISAuthenticated',
    'id_token', 'access_token', 'refresh_token', '.AspNetCore.Cookies'
  ];

  const domains = [
    window.location.hostname,
    `.${window.location.hostname}`,
    '.ford.com', // Specific for Ford environment
    'corp.sts.ford.com'
  ];

  authCookies.forEach(name => {
    domains.forEach(domain => {
      // Try multiple paths and domain combinations
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  });
};

const Navbar = () => {
  const [isActive, setIsActive] = useState(false);
  const [dropDown, setDropDown] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  //const cdsid = useAppSelector(userId);
  const cdsid =  useCdsid(); //'KMANEM2';
  //const cdsid = useAppSelector(userId);
  //const cdsid = useCdsid();
  // Use the same dual-gate (JWT + MDP execute) as ProtectedRoute / FsrAdminGuard
  const { hasAccess: canAccessFsrAdmin } = useFsrAdminAccess();
  const { instance } = useMsal();

  const [redirectLogin, setRedirectLogin] = useState(false); // State to trigger logout

  const toggleActiveClass = () => {
    setIsActive(!isActive);
  };

  const handleDropDownMenu = () => {
    setDropDown(!dropDown);
  };

  const toggleHelpModal = () => {
    setShowHelpModal(!showHelpModal);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && event.target instanceof Node && !modalRef.current.contains(event.target)) {
        setShowHelpModal(false);
      }
    };

    if (showHelpModal) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHelpModal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && event.target instanceof Node && !dropdownRef.current.contains(event.target)) {
        const toggleButton = document.querySelector('[aria-label="Toggle user menu"]');
        if (!toggleButton || !toggleButton.contains(event.target)) {
          setDropDown(false);
        }
      }
    };

    if (dropDown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropDown]);

  // NEW EFFECT TO HANDLE BROWSER BACK BUTTON AND SESSION EXPIRATION
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {

      if (event.persisted) {
        console.log('Page restored from bfcache. Re-validating session...');

        const isUserLoggedInFromRedux = !!cdsid;

        const authCookies = [
          'clientId', 'clientSecret', 'FedAuth', 'FedAuth1', 'MSISAuth',
          'MSISAuthenticated', '.AspNetCore.Cookies', 'JSESSIONID', 'PHPSESSID',
          'sessionid', 'connect.sid', '__Host-Session', '__Secure-Session',
          'XSRF-TOKEN', 'id_token', 'access_token', 'refresh_token', 'oidc.user',
          'auth_token', 'remember_me'
        ];
        const hasAuthCookies = authCookies.some(cookieName => document.cookie.includes(cookieName));

        const hasLocalStorageAuth = Object.keys(localStorage).some(key => key.includes('token') || key.includes('auth') || key.includes('user'));
        const hasSessionStorageAuth = Object.keys(sessionStorage).some(key => key.includes('token') || key.includes('auth') || key.includes('user'));

        if (!isUserLoggedInFromRedux && !hasAuthCookies && !hasLocalStorageAuth && !hasSessionStorageAuth) {
          console.log('Detected unauthenticated state after bfcache. Forcing redirect to login.');
          sessionStorage.clear();
          localStorage.clear();
          const currentDomain = window.location.hostname;
          const adfsBaseLogoutUrl = process.env.REACT_APP_ADFS_LOGOUT_BASE_URL ;
          let adfsDomain = '';
          try {
            if (adfsBaseLogoutUrl) {
              adfsDomain = new URL(adfsBaseLogoutUrl).hostname;
            }
          } catch (e) {
            console.error('Error parsing ADFS logout URL for domain:', e);
          }

          authCookies.forEach(cookieName => {
            clearSpecificCookie(cookieName, currentDomain);
            if (adfsDomain) clearSpecificCookie(cookieName, adfsDomain);
          });

          window.location.replace('/login');
        } else if (isUserLoggedInFromRedux && (!hasAuthCookies || !hasLocalStorageAuth || !hasSessionStorageAuth) ) {
          console.warn('Redux state indicates logged in, but local tokens/cookies are missing. Forcing logout.');
          sessionStorage.clear();
          localStorage.clear();
          const currentDomain = window.location.hostname;
          const adfsBaseLogoutUrl = process.env.REACT_APP_ADFS_LOGOUT_BASE_URL ;
          let adfsDomain = '';
          try {
            if (adfsBaseLogoutUrl) {
              adfsDomain = new URL(adfsBaseLogoutUrl).hostname;
            }
          } catch (e) {
            console.error('Error parsing ADFS logout URL for domain:', e);
          }

          authCookies.forEach(cookieName => {
            clearSpecificCookie(cookieName, currentDomain);
            if (adfsDomain) clearSpecificCookie(cookieName, adfsDomain);
          });
          window.location.replace('/login');
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [cdsid]);


  const handleLogout = async () => {
    console.log("Logging out...");

    // 1. Clear Local/Session Storage
    localStorage.clear();
    sessionStorage.clear();

    // 2. Clear Cookies manually
    clearAllAuthCookies();

    // 3. Trigger MSAL Logout
    // This sends the user to Microsoft to clear HttpOnly cookies
    await instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin + '/',
      onRedirectNavigate: () => {
        // Return false if you want to stop navigation, but we want it to happen
        return true;
      }
    });
  };

  return (
      <div className="w-full">
        {/* Render RedirectToAdfsLogout unconditionally at the top level */}
        {/*It will only perform logout when redirectLogin is true */}
      {/*  <RedirectToAdfsLogout triggerLogout={redirectLogin} />*/}

        {showHelpModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
              <div
                  ref={modalRef}
                  className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
              >
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Help</h2>
                  <button
                      onClick={toggleHelpModal}
                      className="text-gray-500 hover:text-gray-700"
                      aria-label="Close help modal"
                  >
                    <RxCross1 size={20} />
                  </button>
                </div>
                <div className="space-y-4 text-gray-800">
                  <p className="font-medium text-lg">Having trouble using FAD SRS?</p>

                  <p><span className="font-medium">Technical Difficulties:</span> If you have technical difficulties, please email <a href="mailto:dbrothe7@ford.com" className="text-blue-600 hover:underline">dbrothe7@ford.com</a></p>

                  <p><span className="font-medium">Feedback:</span> Share your FAD SRS experience with us by sending comments and suggestions to <a href="mailto:dbrothe7@ford.com" className="text-blue-600 hover:underline">dbrothe7@ford.com</a></p>

                  <p><span className="font-medium">Quick Reference Guide:</span> <a href={`${process.env.PUBLIC_URL}/quick-reference-guide.pdf`} download className="text-blue-600 hover:underline">Click here</a> to open a PDF version.</p>

                  <div className="pt-3 border-t border-gray-300">
                    <p className="font-medium">About FAD Sales Reporting System website</p>
                    <p>Version: fsr_02_Aug_2018-v1.0-PROD</p>
                    <p>Released: 2018-08-02</p>
                  </div>
                </div>
              </div>
            </div>
        )}

        <div className="header w-full">
          <header className="navbar-header sticky top-0 z-50 bg-white flex items-center justify-between px-4">
            {/* Logo - adjusted for better mobile display */}
            <div className="w-auto md:w-[20%] h-full flex items-center">
              <Link to="/">
                <img src={motorCraft_logo} alt="logo" className="h-10 md:h-12 object-contain" />
              </Link>
            </div>

            {/* Desktop Menu - modified width for better responsiveness */}
            <div className="hidden md:block md:w-[50%] lg:w-[90%]">
              <ul className="flex justify-evenly items-center w-full lg:gap-5">

                <li>
                  <Link to="/reports" className="navlink-li">
                    Reports
                  </Link>
                </li>

                <li>
                  <Link to="/fadAdmin" className="navlink-li">
                    FAD Admin
                  </Link>
                </li>

                {canAccessFsrAdmin && (
                    <li>
                      <Link to="/fsrAdmin" className="navlink-li">
                        FSR Admin
                      </Link>
                    </li>
                )}

                <li>
                  <Link to="https://www.fmcdealer.dealerconnection.com/content/mcd/us/en/Home/Reporting.html?utm_source=redirect&utm_medium=sharepoint&utm_campaign=/sites/MCDistributors/FAD/Reporting/Pages/default.aspx"
                        className="navlink-li"
                        target='_blank'
                        rel='noopener noreferrer'
                  >
                    MC Distributor
                  </Link>
                </li>

              </ul>
            </div>

            {/* User Button for Desktop (hidden on mobile) */}
            <div className="user-button hidden md:flex items-center ml-auto">
              <span className='text-sm mr-2'>{cdsid || 'User'}</span>
              <button
                  onClick={handleDropDownMenu}
                  aria-label="Toggle user menu"
                  className="flex items-center"
              >
                {!dropDown ? <IoIosArrowDropdown size={20} data-testid="dropdown-icon"/> : <IoIosArrowDropup size={20} data-testid="dropup-icon"/>}
              </button>

              {dropDown && (
                  <div
                      ref={dropdownRef}
                      className="w-[200px] md:w-[250px] z-50 absolute top-[75px] right-0 bg-white text-black p-4 rounded-lg shadow-lg"
                      role="menu"
                  >
                    <ul className="flex flex-col gap-5">
                      <li className="navlink-li" role="menuitem">
                        <a
                            href={`${process.env.PUBLIC_URL}/faq.pdf`}
                            download="FADSRS-FAQ.pdf"
                            className="w-full inline-block"
                        >
                          FAQ
                        </a>
                      </li>
                      <li className="navlink-li" role="menuitem">
                        <a
                            href="mailto:dbrothe7@ford.com"
                            className="w-full inline-block"
                        >
                          Contact Us
                        </a>
                      </li>
                      <li className="navlink-li" role="menuitem">
                        <button
                            onClick={toggleHelpModal}
                            className="text-left w-full"
                        >
                          Help
                        </button>
                      </li>
                      <li className="navlink-li" role="menuitem">
                        <button
                            //onClick={() => setRedirectLogin(true)} // This sets the state
                            onClick={handleLogout}
                            className="text-left w-full"
                        >
                          {' '}Logout{' '}
                        </button>
                      </li>
                      {/* Removed the conditional rendering here: {redirectLogin && <RedirectToAdfsLogout />} */}
                    </ul>
                  </div>
              )}
            </div>

            {/* Mobile Hamburger Button - adjusted positioning */}
            <button
                type="button"
                className="block md:hidden border-none rounded-md bg-[#fff] text-base p-2 font-semibold ml-2"
                onClick={toggleActiveClass}
                aria-label={isActive ? "Close menu" : "Open menu"}
                aria-expanded={isActive}
            >
              {!isActive ? <img src={Hamburger} alt="" className="h-6 w-6" /> : <RxCross1 className="h-6 w-6" data-testid="cross-icon"/>}
            </button>
          </header>

          {/* Mobile Menu Panel - improved positioning and styling */}
          <div
              className={`bg-white w-full block md:hidden transition-all duration-500 shadow-md ${
                  isActive ? 'h-auto' : 'h-0 overflow-hidden'
              } absolute left-0 right-0 z-40`}
              id="mobile-menu"
              data-testid="mobile-menu-container"
              aria-hidden={!isActive}
          >
            <ul className="flex flex-col gap-4 p-4">
              <li>
                <Link to="/" className="navlink-li-mobile">
                  Home
                </Link>
              </li>

              <li>
                <Link to="/reports" className="navlink-li-mobile">
                  Reports
                </Link>
              </li>

              <li>
                <Link to="/fadAdmin" className="navlink-li-mobile">
                  FAD Admin
                </Link>
              </li>

              {canAccessFsrAdmin && (
                  <li>
                    <Link to="/fsrAdmin" className="navlink-li-mobile">
                      FSR Admin
                    </Link>
                  </li>
              )}

              <li>
                <Link to="https://www.fmcdealer.dealerconnection.com/content/mcd/us/en/Home/Reporting.html?utm_source=redirect&utm_medium=sharepoint&utm_campaign=/sites/MCDistributors/FAD/Reporting/Pages/default.aspx"
                      className="navlink-li-mobile"
                      target='_blank'
                      rel='noopener noreferrer'
                >
                  MC Distributor
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
  );
};

export default Navbar;
